import type { UserCallout } from '@/services/userCallouts';

/**
 * The expert's progress through the SMS opt-in nudge, derived from the
 * server-backed `expert_first_message_sms` callout (not stored as an enum):
 * - `unset`: never dismissed — the full setup modal is still in play.
 * - `banner`: dismissed the modal once (`dismissedCount > 0`) — show the
 *   lighter banner on every inquiry instead.
 * - `silenced`: dismissed the banner too (`resolvedAt` set) — stop nudging.
 */
export type ExpertSmsPromptStage = 'unset' | 'banner' | 'silenced';

/**
 * Derive the nudge stage from the callout row. A `resolvedAt` marker silences
 * the nudge regardless of count; any dismissal short of that de-escalates the
 * modal to the banner. An untouched (or absent) callout keeps the modal in play.
 *
 * `resolvedAt` takes precedence over `dismissedCount`: a resolved callout is
 * `silenced` even with `dismissedCount` 0, so the terminal opt-out can never be
 * misread as the intermediate banner stage.
 */
export function deriveExpertSmsPromptStage(callout: UserCallout | undefined): ExpertSmsPromptStage {
  if (!callout) {
    return 'unset';
  }

  if (callout.resolvedAt) {
    return 'silenced';
  }

  return callout.dismissedCount > 0 ? 'banner' : 'unset';
}
